import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { providePrimeNG } from 'primeng/config';
import {
  buildPreset,
  DARK_MODE_CLASS,
  applyColorScheme,
  resolveTheme,
} from './layout/service/app-theme';
import {
  LAYOUT_CONFIG_DEFAULTS,
  mergeLayoutConfigWithDefaults,
  readStoredLayoutConfig,
} from './layout/service/layout-config-storage';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { httpInterceptorProviders } from './http-interceptors';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppLayoutModule } from './layout/app.layout.module';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';

const isIE =
  window.navigator.userAgent.indexOf('MSIE ') > -1 ||
  window.navigator.userAgent.indexOf('Trident/') > -1;

const msalConfig = {
  auth: {
    clientId: 'b843d78c-a646-42c7-9c7b-7ad8828d160e',
    authority:
      'https://login.microsoftonline.com/6fe48ad2-8b7b-483a-8f25-28fe1a030a74',
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: isIE, // Recommended to set to true for Internet Explorer
  },
};

/**
 * Creates the MSAL instance. It is NOT initialized here: msal-browser 4 needs
 * `initialize()` to finish before any other call, which `provideAppInitializer`
 * below does through `MsalService.initialize()` before the app starts. Keep this
 * private to the app config so nothing can use the instance before that.
 */
function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication(msalConfig);
}

function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['openid', 'profile', 'email'],
    },
  };
}

const initialTheme = resolveTheme(
  mergeLayoutConfigWithDefaults(
    LAYOUT_CONFIG_DEFAULTS,
    readStoredLayoutConfig(),
  ).theme,
);
applyColorScheme(initialTheme.dark);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    providePrimeNG({
      theme: {
        preset: buildPreset(initialTheme),
        options: {
          darkModeSelector: `.${DARK_MODE_CLASS}`,
          cssLayer: false,
        },
      },
    }),
    provideHttpClient(withInterceptorsFromDi()),
    importProvidersFrom(BrowserModule),
    importProvidersFrom(BrowserAnimationsModule),
    importProvidersFrom(AppLayoutModule),
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    MsalService,
    // msal-browser 4 requires initialize() to finish before any other API call.
    // Angular awaits app initializers before the router's initial navigation and
    // before any HTTP interceptor runs, and MsalGuard/MsalBroadcastService are
    // only constructed on first injection (the first guarded route), so none of
    // them can touch the MSAL instance before initialize() resolves.
    provideAppInitializer(() => inject(MsalService).initialize()),
    MsalGuard,
    // Built on first guard use, after initialize() and before any redirect event.
    // No app code listens to MSAL events, and MSAL_INITIALIZED sets no inProgress$ state.
    MsalBroadcastService,
    httpInterceptorProviders,
  ],
};
